import { useState } from "react";
import { formatRange } from "../utils/time";
import { KacBadge } from "../components/KacMark";
import { API_URL } from "../utils/api";

// Public, no-login page for members to look up their group & time by email.
// Reached at "/?schedule" (see App.js), served by the unauthenticated
// GET /api.php?lookup=<email> endpoint. One email covers several people —
// anyone registered under it, plus the rest of their household, so either
// parent's address brings up both parents and the kids (households come from
// the club's member import file; see src/utils/households.js). The response
// is a list of people, each with their own group(s).
export default function MemberScheduleView() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}?lookup=${encodeURIComponent(trimmed)}`);
      if (!res.ok) throw new Error("Lookup failed");
      const data = await res.json();
      setResult(data);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // The people registered under the typed address come first; the rest of
  // the household follows in roster order.
  const people = [...(result?.people ?? [])].sort(
    (a, b) => (b.sharesEmail === true) - (a.sharesEmail === true)
  );
  const searched = result !== null;
  const householdCount = people.filter(p => p.sharesEmail === false).length;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-6 sm:p-8">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6 mt-4">
          <KacBadge className="mb-4" size="w-16 h-16 text-3xl rounded-2xl" />
          <p className="font-display text-brand-blue text-lg tracking-wide">Kings Athletic Club</p>
          <h1 className="text-3xl font-display text-brand-blue">My Schedule</h1>
          <p className="text-gray-500 text-sm mt-1">
            Enter the email you registered with to see your whole family's groups and times.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(null); }}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2 rounded-lg text-sm transition-colors"
          >
            {loading ? "Looking up…" : "Find my schedule"}
          </button>
        </form>

        {searched && people.length === 0 && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6 text-center">
            <p className="text-gray-700 font-medium">No registration found for that email.</p>
            <p className="text-gray-500 text-sm mt-1">
              Double-check the spelling, or contact the club if you think this is a mistake.
            </p>
          </div>
        )}

        {people.length > 1 && (
          <p className="mt-6 -mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide text-center">
            {householdCount > 0
              ? `Your household · ${people.length} people`
              : `${people.length} people on this email`}
          </p>
        )}

        {people.map((person, i) => (
          <div key={i} className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <h2 className="font-display text-xl text-brand-blue">{person.name}</h2>
            </div>

            {person.groups.length === 0 ? (
              <div className="px-5 py-4 text-sm text-gray-500">
                Not placed in a group yet — check back soon or contact the club.
              </div>
            ) : (
              person.groups.map((g, j) => {
                const groupmates = g.members.filter(m => m !== person.name);
                const label = g.kind === "private" ? "Private lesson"
                  : g.kind === "openplay" ? "Open play" : null;
                return (
                  <div key={j} className="px-5 py-4 border-b border-gray-100 last:border-b-0">
                    {label && (
                      <span className={`inline-block text-xs font-semibold uppercase tracking-wide rounded px-2 py-0.5 mb-2 ${
                        g.kind === "private" ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"
                      }`}>
                        {label}
                      </span>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="font-display text-2xl text-brand-blue">{g.day}</span>
                      <span className="text-brand-red font-semibold">
                        {g.startTime && g.duration ? formatRange(g.startTime, g.duration) : g.startTime}
                      </span>
                    </div>
                    {g.court && (
                      <p className="text-sm text-gray-600 mt-1">📍 {g.court}</p>
                    )}
                    {g.coach && (
                      <p className="text-sm text-gray-600 mt-1">🎾 Coach: {g.coach}</p>
                    )}
                    {groupmates.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                          {g.kind === "openplay" ? "Playing with" : "In this group"}
                        </p>
                        <p className="text-sm text-gray-700">{groupmates.join(", ")}</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
